import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTutorial } from '../../hooks/useTutorial';
import { getTutorialSteps } from './tutorialSteps';
import TutorialOverlay from './TutorialOverlay';

/**
 * Tutorial Component
 * 
 * Main tutorial component that manages the interactive tour
 * Can be triggered from menu or automatically for new users
 */
const Tutorial = ({ user, isActive: externalActive, onClose: externalOnClose }) => {
  const location = useLocation();
  const tutorialSteps = getTutorialSteps(location.pathname);
  const userRole = user?.roles?.[0]?.role_name?.toLowerCase() || 'viewer';

  const {
    currentStep,
    currentStepIndex,
    totalSteps,
    isActive: internalActive,
    startTutorial,
    nextStep,
    previousStep,
    closeTutorial,
    skipTutorial,
    goToStep,
    setIsActive
  } = useTutorial(tutorialSteps, userRole);

  // Use external active state if provided, otherwise use internal
  const isActive = externalActive !== undefined ? externalActive : internalActive;

  // Handle close (saves progress for later continuation)
  const handleClose = () => {
    // Save progress via hook
    closeTutorial();
    // Also notify external controller
    if (externalOnClose) {
      externalOnClose();
    }
  };

  // Handle skip - marks as completed, won't show again
  const handleSkip = () => {
    skipTutorial();
    if (externalOnClose) {
      externalOnClose();
    }
  };

  // Auto-start if external active is set
  useEffect(() => {
    if (externalActive && tutorialSteps.length > 0 && !internalActive) {
      // Small delay to ensure component is mounted
      const timer = setTimeout(() => {
        startTutorial(0);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [externalActive, tutorialSteps.length, internalActive, startTutorial]);

  // If external active becomes false, stop internal tutorial
  useEffect(() => {
    if (externalActive === false && internalActive) {
      setIsActive(false);
    }
  }, [externalActive, internalActive, setIsActive]);

  // Don't render if not active or no steps
  if (!isActive || tutorialSteps.length === 0) {
    return null;
  }

  // If active but no current step yet, wait for it
  if (!currentStep) {
    return null;
  }

  return (
    <TutorialOverlay
      step={currentStep}
      onNext={nextStep}
      onPrevious={previousStep}
      onClose={handleClose}
      onSkip={handleSkip}
      onGoToStep={goToStep}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
    />
  );
};

export default Tutorial;
